import { connect } from "react-redux";
import * as React from "react";

import { aggregatedSetToModel, setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { queryPicklistAction } from "../../../Engine/ActionCreators/PicklistActionCreators";
import { PicklistDataItem } from "../../../Engine/Core/Picklists/PicklistTypes";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { EvaluableProp, ModelAccessor, withEvaluators } from "../../withEvaluators";
import { withMetrics } from "../../withMetrics";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { FilterBuilder, ResolveToAllFilter, ResolveToNoneFilter } from "../Picklist/Common/FilterBuilder";
import { DigestQueryFilter } from "../../../../../Common/CandyApiClient/DigestApiClient/Types/DigestQueryFilter";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { PicklistSourceId } from "../../../Engine/Core/Picklists/IPicklistsProvider";
import { withFocusOnNextValueEditor } from "../../../Helpers/JumpByEnterOnNextControl/WithFocusOnNextValueEditor";
import { checkIsSuitableToClick, externalOnClickHandler } from "../../../Engine/ActionCreators/ExternalOnClickAction";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { schedulerWrapper } from "../../../Helpers/Scheduler/SchedulerApi";
import { WithHighlightedInjectableProps, withHighlighting } from "../../withHighlighting";
import { nonRegularFieldName } from "../../../Engine/Core/CoreTypes";

import { ComboBox } from "./ComboBox";
import { ComboBoxDataItem, ComboboxDependency } from "./ComboboxItemProvider";

export interface ComboBoxContainerProps
    extends PicklistSourceId,
        FormActionHandlerExecutionInjectableProps,
        WithHighlightedInjectableProps {
    disabled?: boolean;
    viewModelSelect: ViewModelSelect;
    bindingPath: AbsoluteResolvedModelPath;
    filter?: (
        builder: FilterBuilder,
        getPath: ModelAccessor,
        none: ResolveToNoneFilter,
        all: ResolveToAllFilter
    ) => null | undefined | FilterBuilder | ResolveToNoneFilter | ResolveToAllFilter;
    mostLikelyFilter?: (
        builder: FilterBuilder,
        getPath: ModelAccessor,
        none: ResolveToNoneFilter,
        all: ResolveToAllFilter
    ) => undefined | null | FilterBuilder | ResolveToNoneFilter | ResolveToAllFilter;
    notFoundText?: string;
    mostLikelyCaption?: string;
    emptyValueFilter?: (
        builder: FilterBuilder,
        getPath: ModelAccessor,
        none: ResolveToNoneFilter,
        all: ResolveToAllFilter
    ) => null | undefined | FilterBuilder | ResolveToNoneFilter | ResolveToAllFilter;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    disabled2?: EvaluableProp<boolean>;
    formula?: EvaluableProp<React.ReactNode>;
    auto?: boolean;
    autoValue?: string;
    isEnableCaseIndependentValidation?: boolean;
    callHelperOnBlur?: FormActionHandler;
    callHelperOnChange?: FormActionHandler;
    filterCallback?: FormActionHandler;
    mapChangesField: string[];
    dependencies: ComboboxDependency[];
}

const mapStateToProps = (state: FullFormState, ownProps: ComboBoxContainerProps) => {
    const { disabled, bindingPath, viewModelSelect, auto } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);
    const autoFlag = auto && controlModel.autoFlag;
    const autoValue = auto ? (ownProps.autoValue ?? controlModel.autoValue) : undefined;

    return {
        disabled: ownProps.processEvaluableProp(state, ownProps.disabled2) || (disabled ?? controlModel.disabled),
        value: autoFlag ? controlModel.autoValue : controlModel.value,
        id: controlModel.id,
        description: controlModel.description,
        error: controlModel.error,
        warning: controlModel.warning,
        isTooltipEnabled: controlModel.isTooltipEnabled,
        notFoundText: ownProps.notFoundText,
        mostLikelyCaption: ownProps.mostLikelyCaption,
        highlighted: ownProps.highlighted,
        isReadOnly: state.settings.isReadOnly,
        isLink: checkIsSuitableToClick(state.settings, bindingPath),
        autoFlag: autoFlag,
        autoValue: autoValue,
        formula: ownProps.processEvaluableProp(state, ownProps.formula),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: ComboBoxContainerProps) => {
    const { bindingPath, dependencies, mapChangesField } = ownProps;

    const mostLikelyFilter = ownProps.mostLikelyFilter;
    const mostLikelyBindedFilter =
        mostLikelyFilter != undefined
            ? (builder: FilterBuilder, none: ResolveToNoneFilter, all: ResolveToAllFilter) =>
                  dispatch((_, getState) =>
                      ownProps.processEvaluableProp(getState(), {
                          type: "ModelDependentEvaluator",
                          expression: (x: ModelAccessor) => mostLikelyFilter(builder, x, none, all),
                      })
                  )
            : undefined;

    const filter = ownProps.filter;
    const bindedFilter =
        filter != undefined
            ? (builder: FilterBuilder, none: ResolveToNoneFilter, all: ResolveToAllFilter) =>
                  dispatch((_, getState) =>
                      ownProps.processEvaluableProp(getState(), {
                          type: "ModelDependentEvaluator",
                          expression: (x: ModelAccessor) => filter(builder, x, none, all),
                      })
                  )
            : undefined;
    const emptyValueFilter = ownProps.emptyValueFilter;
    const emptyValueBindedFilter =
        emptyValueFilter != undefined
            ? (builder: FilterBuilder, none: ResolveToNoneFilter, all: ResolveToAllFilter) =>
                  dispatch((_, getState) =>
                      ownProps.processEvaluableProp(getState(), {
                          type: "ModelDependentEvaluator",
                          expression: (x: ModelAccessor) => emptyValueFilter(builder, x, none, all),
                      })
                  )
            : undefined;

    return {
        onChange: async (value: string | number, item?: PicklistDataItem) => {
            if (item && mapChangesField) {
                for (const fieldName of mapChangesField) {
                    dependencies
                        .filter(dependency => dependency.field === fieldName && dependency.path)
                        .forEach(dependency => {
                            dispatch(
                                setToModel(
                                    dependency.path,
                                    nonRegularFieldName(dependency.value) || "value",
                                    String(item[fieldName] ?? "")
                                )
                            );
                        });
                }
            } else {
                dispatch(setToModel(bindingPath, "value", value));
            }

            if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnChange);
            }
        },
        onBlur: async () => {
            if (isNotNullOrUndefined(ownProps.callHelperOnBlur)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnBlur);
            }
        },
        filterCallback: isNotNullOrUndefined(ownProps.filterCallback)
            ? async (items: Array<ComboBoxDataItem | JSX.Element>): Promise<Array<ComboBoxDataItem | JSX.Element>> => {
                  if (isNotNullOrUndefined(ownProps.filterCallback)) {
                      const newItems = (await ownProps.executeFormActionHandler(ownProps.filterCallback, items)) as
                          | ComboBoxDataItem[]
                          | undefined;
                      return newItems ?? items;
                  }
                  return items;
              }
            : undefined,
        setDescription: (name: string | undefined): void =>
            dispatch(aggregatedSetToModel(bindingPath, "description", name)),
        setPicklistDataItem: (value: undefined | PicklistDataItem): void =>
            schedulerWrapper(() => dispatch(setToModel(bindingPath, "picklist", value))),
        setItemId: (id: string | undefined): void => dispatch(setToModel(bindingPath, "id", id)),
        onQueryPicklist: (queryFilter: DigestQueryFilter) => dispatch(queryPicklistAction(ownProps, queryFilter)),
        filter: bindedFilter,
        mostLikelyFilter: mostLikelyBindedFilter,
        emptyValueFilter: emptyValueBindedFilter,
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
    };
};

export const ComboBoxContainer = withMetrics(
    withFocusOnNextValueEditor(
        withResolvePaths(
            withFormActionHandlerExecution(
                withViewModelSelect(
                    withEvaluators(
                        withHighlighting(
                            connect(
                                mapStateToProps,
                                mapDispatchToProps
                            )(withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(ComboBox))))
                        )
                    )
                )
            )
        )
    )
);
